import { InvalidateOptions, QueryClient, InvalidateQueryFilters } from "@tanstack/react-query";
import { ParamsForMethod, QueryDataForMethod, QueryMethodForModel, ModelName, TellescopeQueryKey } from "../../types";
import { TellescopeInvalidateQueryFilters } from "../../hooks";
import { invalidateTellescopeQueries } from "./invalidate-tellescope-queries";

/**
 * Arguments for invalidating all queries for a specific Tellescope model.
 *
 * @template Model - The Tellescope model name (must support query methods)
 * @template TError - The error type for the queries (defaults to Error)
 */
export interface InvalidateTellescopeModelQueriesArgs<Model extends ModelName, TError = Error> {
  /** The Tellescope model to invalidate queries for */
  model: Model;
  /** The TanStack Query client instance */
  queryClient: QueryClient;
  /**
   * Optional filters applied to all queries for this model.
   * These filters are combined with method-specific filters if both are provided.
   */
  filters?: Omit<
    TellescopeInvalidateQueryFilters<
      QueryDataForMethod<Model, QueryMethodForModel<Model>>,
      TError,
      QueryDataForMethod<Model, QueryMethodForModel<Model>>,
      TellescopeQueryKey<Model, QueryMethodForModel<Model>>
    >,
    "queryKey"
  >;
  /** Optional invalidate options (e.g., throwOnError, cancelRefetch) applied to all invalidations */
  options?: InvalidateOptions;
  /**
   * Optional method-specific filters. Allows invalidating queries for specific methods
   * and optionally matching by request parameters.
   */
  methodFilters?: Partial<{
    [Method in QueryMethodForModel<Model>]: Omit<
      TellescopeInvalidateQueryFilters<
        QueryDataForMethod<Model, Method>,
        TError,
        QueryDataForMethod<Model, Method>,
        TellescopeQueryKey<Model, Method>
      >,
      "queryKey"
    > & {
      /** Optional request parameters to match for this method */
      request?: Partial<ParamsForMethod<Model, Method>>;
      /** Optional method-specific invalidate options that override global options */
      options?: InvalidateOptions;
      /** Delay in ms before invalidating matching queries. Useful when the backend needs time to process before a refetch would return updated data. */
      delay?: number;
    };
  }>;
}

/**
 * Invalidates all cached queries for a specific Tellescope model.
 *
 * This function provides a way to invalidate all queries for a model, optionally
 * with method-specific filters. When no methodFilters are provided, it invalidates
 * all queries for the model. When methodFilters are provided, it only invalidates
 * queries for the specified methods.
 *
 * @template Model - The Tellescope model name (must support query methods)
 * @template TError - The error type for the queries (defaults to Error)
 *
 * @param args - Configuration object containing model, queryClient, and optional filters
 * @returns A promise that resolves when invalidation is complete
 *
 * @example
 * ```ts
 * // Invalidate all user queries
 * await invalidateTellescopeModelQueries({
 *   queryClient,
 *   model: "users",
 * });
 * ```
 *
 * @example
 * ```ts
 * // Invalidate only specific methods
 * await invalidateTellescopeModelQueries({
 *   queryClient,
 *   model: "users",
 *   methodFilters: {
 *     getOne: {
 *       // Invalidate all getOne queries
 *     },
 *     getSome: {
 *       request: { filter: { fname: "John" }, limit: 10 },
 *       refetchType: "active", // Only refetch active queries
 *     },
 *   },
 * });
 * ```
 *
 * @example
 * ```ts
 * // Use global filters with method-specific filters
 * await invalidateTellescopeModelQueries({
 *   queryClient,
 *   model: "users",
 *   filters: {
 *     refetchType: "none", // Don't auto-refetch any queries
 *   },
 *   methodFilters: {
 *     getOne: {
 *       // Inherits refetchType: "none" from global filters
 *     },
 *   },
 * });
 * ```
 *
 * @example
 * ```ts
 * // Use global and method-specific options
 * await invalidateTellescopeModelQueries({
 *   queryClient,
 *   model: "users",
 *   options: {
 *     throwOnError: true, // Global option for all methods
 *   },
 *   methodFilters: {
 *     getOne: {
 *       options: {
 *         cancelRefetch: false, // Method-specific option (overrides global)
 *       },
 *     },
 *   },
 * });
 * ```
 */
export const invalidateTellescopeModelQueries = async <Model extends ModelName, TError = Error>({
  model,
  queryClient,
  filters,
  methodFilters,
  options,
}: InvalidateTellescopeModelQueriesArgs<Model, TError>) => {
  const methodNames = Object.keys(methodFilters ?? {}) as QueryMethodForModel<Model>[];
  // If no method filters are specified, invalidate all queries for the model
  if (!methodFilters || !methodNames.length) {
    return queryClient.invalidateQueries(
      {
        queryKey: [model],
        exact: false,
        ...filters,
      } as InvalidateQueryFilters<Model[]>,
      options
    );
  }

  // Otherwise, invalidate queries for each specified method
  const invalidationPromises = methodNames.map(<Method extends QueryMethodForModel<Model>>(method: Method) => {
    const { request, options: methodOptions, delay, ...methodSpecificFilters } = methodFilters[method] ?? {};

    const invalidate = () =>
      invalidateTellescopeQueries<Model, Method, TError>({
        model,
        method,
        request,
        queryClient,
        options: { ...options, ...methodOptions },
        ...filters,
        ...methodSpecificFilters,
      });

    if (delay) {
      // Fire-and-forget: schedule the invalidation after the delay
      setTimeout(invalidate, delay);
      return;
    }

    return invalidate();
  });

  await Promise.all(invalidationPromises);
};
